/**
 * Plain-text helpers for inbox list titles and preview snippets.
 * List rows must not show raw markdown (##, **, tables, fences).
 */

/**
 * Strip common markdown markers to readable plain text for list UI.
 */
export function stripInboxMarkdown(input: string): string {
  let text = String(input ?? '')
  if (!text) return ''

  // Fenced code -> drop
  text = text.replace(/```[\s\S]*?```/g, ' ')
  // Images ![alt](url) -> alt
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
  // Links [text](url) -> text
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
  // Inline code
  text = text.replace(/`([^`]+)`/g, '$1')
  // Headings
  text = text.replace(/^#{1,6}\s+/gm, '')
  // Bold / italic (non-greedy inner)
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1')
  text = text.replace(/__([^_]+)__/g, '$1')
  text = text.replace(/\*([^*]+)\*/g, '$1')
  text = text.replace(/_([^_]+)_/g, '$1')
  // Strikethrough
  text = text.replace(/~~([^~]+)~~/g, '$1')
  // Blockquotes
  text = text.replace(/^>\s?/gm, '')
  // List markers
  text = text.replace(/^[\t ]*[-*+]\s+/gm, '')
  text = text.replace(/^[\t ]*\d+\.\s+/gm, '')
  // Table separator rows and leftover pipes
  text = text.replace(/^\|?[\t :|-]+\|[\t |:-]*$/gm, ' ')
  text = text.replace(/\|/g, ' ')
  // Horizontal rules
  text = text.replace(/^(-{3,}|\*{3,}|_{3,})\s*$/gm, ' ')
  // Leftover unpaired emphasis markers (e.g. "** DISCUSS: ...")
  text = text.replace(/\*\*/g, '')
  text = text.replace(/(?<!\w)_(?!\w)/g, '')
  text = text.replace(/(?<!\w)\*(?!\w)/g, '')

  return text.replace(/\s+/g, ' ').trim()
}

/**
 * Truncate plain text for inbox preview rows.
 */
export function toInboxPreviewPlain(input: string, maxLen = 140): string {
  const plain = stripInboxMarkdown(input)
  if (!plain) return ''
  if (plain.length <= maxLen) return plain
  return `${plain.slice(0, Math.max(0, maxLen - 3)).trimEnd()}...`
}

/**
 * Titles are usually short - strip markers without aggressive truncation.
 */
export function toInboxTitlePlain(input: string): string {
  return stripInboxMarkdown(input)
}

/**
 * Prefer a human summary over raw body start when building list previews.
 */
export function pickInboxPreviewSource(parts: {
  summary?: unknown
  bodyPreview?: unknown
  preview?: unknown
  snippet?: unknown
  body?: unknown
}): string {
  const candidates = [
    parts.summary,
    parts.bodyPreview,
    parts.preview,
    parts.snippet,
    parts.body,
  ]
  for (const c of candidates) {
    const s = typeof c === 'string' ? c.trim() : ''
    if (s) return s
  }
  return ''
}
